import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { computeTail, HeadAnchor, headPoint, TailGeometry } from "./dialogQuizTails";

const sameTails = (a: Record<string, TailGeometry>, b: Record<string, TailGeometry>) => {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => b[key] && a[key].y === b[key].y && a[key].dy === b[key].dy)
  );
};

/**
 * Measures the answer bubbles against the actor image and aims every tail at
 * the head. Re-measures when the popup, a bubble (longer text) or the image
 * changes size.
 */
export default function useDialogQuizTails(
  anchor: HeadAnchor,
  actorOnLeft: boolean,
  layoutKey: string
) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const bubbles = useRef(new Map<string, HTMLElement>());
  const [tails, setTails] = useState<Record<string, TailGeometry>>({});

  const measure = useCallback(() => {
    const image = imageRef.current;
    if (!image) return;
    const imageBox = image.getBoundingClientRect();
    if (imageBox.width === 0 || imageBox.height === 0) return;

    const head = headPoint(imageBox, anchor);
    const next: Record<string, TailGeometry> = {};
    bubbles.current.forEach((element, id) => {
      next[id] = computeTail(element.getBoundingClientRect(), head, actorOnLeft);
    });
    setTails((previous) => (sameTails(previous, next) ? previous : next));
  }, [anchor, actorOnLeft]);

  const registerBubble = useCallback(
    (id: string) => (element: HTMLElement | null) => {
      if (element) bubbles.current.set(id, element);
      else bubbles.current.delete(id);
    },
    []
  );

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => measure());
    if (imageRef.current) observer.observe(imageRef.current);
    bubbles.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [measure, layoutKey]);

  return { tails, imageRef, registerBubble, measure };
}
